<template>
    <div class="flex-col profile-container">
        <div class="profile-details">
            <div class="pb-1">
                <button
                    type="button"
                    class="copy-token-button left-align"
                    @click="copyToken()"
                >
                    {{ $t('copyToken') }}
                    <font-awesome-icon icon="copy" />
                </button>
            </div>
            <div class="pb-1">
                <a
                    :href="swaggerURL"
                    target="_blank"
                    class="swagger-link"
                >
                    {{ $t('openSwagger') }}
                </a>
            </div>
            <div class="flex-col flex-3 pb-1">
                <div class="version">
                    {{ $t('version') }}&nbsp;{{ version }}
                </div>
                <div class="version ">
                    {{ $t('apiUrl') }}&nbsp;
                    <a
                        :href="apiUrl"
                        class="swagger-link"
                    >
                        {{ apiUrl }}
                    </a>
                </div>
            </div>
            <div class="contact-ai-wrapper">
                <button
                    type="button"
                    class="contact-ai-wrapper__button"
                    @click="openQueryHub()"
                >
                    <span class="profile-action-icon" />
                    {{ $t('profile.contactAI') }}
                </button>
            </div>
            <div class="send-feedback-wrapper">
                <button
                    type="button"
                    class="send-feedback-wrapper__button"
                    @click="sendToFeedbackForm()"
                >
                    {{ $t('sendFeedback') }}
                </button>
            </div>
            <div class="report-incident-wrapper">
                <button
                    type="button"
                    class="report-incident-wrapper__button"
                    @click="sendToIncidentReporting()"
                >
                    {{ $t('reportIncident') }}
                </button>
            </div>
            <div>
                <button
                    type="button"
                    class="logout-button"
                    @click="logOut()"
                >
                    {{ $t('signOut') }}
                </button>
            </div>
        </div>
        <input
            type="text"
            class="copy-text"
            id="copyText"
        >
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import { EventBus } from '@/utils/eventbus';
import clickOutside from '@/directives/click-outside';
import { APP_SCOPE, sdk } from '@/utils/sdk';
import { MenuEntry } from 'adam.ui-core/dist/src/lib/components/AppDrawer/AppDrawer';

const QUERY_HUB_USE_CASE_IDS: Record<string, number> = { prd: 106, uat: 105, dev: 218, development: 218 };

@Component({
    directives: {
        clickOutside,
    },
})
export default class UserProfile extends Vue {
    @Prop({ default: () => [] }) private applications!: MenuEntry[];

    private version: string | undefined = process.env.VUE_APP_VERSION;
    private apiUrl: string | undefined = process.env.VUE_APP_API_BASE_URL;
    private swaggerURL: string | undefined = process.env.VUE_APP_API_SWAGGER_URL;
    private detailsToggled = false;

    private logOut(): void {
        sdk.auth.logout()
    }

    private sendToFeedbackForm(): void {
        const useCaseId = Number(process.env.VUE_APP_USE_CASE_ID ?? 0);
        const appStoreUrl = `${process.env.VUE_APP_EXTERNAL_APP_APPSTORE_URL}/contact-page/${useCaseId}?isFeedback=true`;

        window.open(appStoreUrl, '_blank');
    }

    private sendToIncidentReporting(): void {
        const useCaseId = Number(process.env.VUE_APP_USE_CASE_ID ?? 0);
        const appStoreUrl = `${process.env.VUE_APP_EXTERNAL_APP_APPSTORE_URL}/incident-page/${useCaseId}`;

        window.open(appStoreUrl, '_blank');
    }

    private openQueryHub(): void {
        const env = process.env.VUE_APP_ENVIRONMENT as string;
        const queryHubUseCaseId = QUERY_HUB_USE_CASE_IDS[env];
        if (!queryHubUseCaseId) { return; }

        const queryHubApp = this.applications.find(
            (app: MenuEntry) => String(app.id) === String(queryHubUseCaseId)
        );
        if (!queryHubApp?.url) { return; }

        const problemMessage = (this.$t('queryHub.redirect.problemMessage') as string) || 'I have a problem with';
        const appName = this.$t('appTitle') as string;
        const prompt = encodeURIComponent(`${problemMessage} ${appName}`);
        window.open(`${queryHubApp.url}?prompt=${prompt}`, '_blank');
    }

    private async copyToken(): Promise<void> {
        const textElement: any = document.getElementById('copyText');
        if (textElement) {
            const token = await sdk.auth.getToken(APP_SCOPE)
            textElement.value = '';
            textElement.value = `Bearer ${token}`;
            textElement.select();
            document.execCommand('copy');
            EventBus.$emit(EventBus.GLOBAL.SHOW_SNACKBAR, 'copiedToClipboard');
        } else {
            throw new Error('Copy text element is undefined');
        }
    }

    private offFocus(): void {
        if (this.detailsToggled) {
            this.detailsToggled = false;
        }
    }
}
</script>

<style scoped lang="scss">
.profile-details {
    color: $dark-blue-grey;
    z-index: 10;
    font-size: 1.6rem;
    right: 0;

    .contact-ai-wrapper {
        @include rem(padding, 0 0 pui-spacing(xxs));

        &__button {
            height: 3.5rem;
            width: 100%;
            transition: 0.2s ease-in;
            background: $light-grey;

            &:hover {
                transition: 0.2s ease-in;
                background: $warm-grey-25;
            }
        }
    }

    .profile-action-icon {
        display: inline-block;
        width: 1.8rem;
        height: 1.8rem;
        vertical-align: top;
        background-color: currentColor;
        -webkit-mask: url('~@/assets/icons/coode-ai-robot-icon.svg') no-repeat center;
        mask: url('~@/assets/icons/coode-ai-robot-icon.svg') no-repeat center;
        -webkit-mask-size: contain;
        mask-size: contain;
    }

    .send-feedback-wrapper {
        @include rem(padding, 0 0 pui-spacing(xxs));

        &__button {
            height: 3.5rem;
            width: 100%;
            transition: 0.2s ease-in;
            color: $white;
            background: $uniper-blue;

            @include rem(border-radius, pui-spacing(l));

            &:hover {
                transition: 0.2s ease-in;
                background: $dark-blue;
            }
        }
    }

    .report-incident-wrapper {
        @include rem(padding, 0 0 pui-spacing(xxs));

        &__button {
            height: 3.5rem;
            width: 100%;
            color: $contextual-red;
            background: $light-grey;
            transition: 0.2s ease-in;
            border-radius: 0;

            &:hover {
                cursor: pointer;
                transition: 0.2s ease-in;
                background: $warm-grey-25;
            }
        }
    }

    .logout-button {
        height: 3.5rem;
        width: 100%;
        color: $dark-grey;
        background: $light-grey;
        transition: 0.2s ease-in;
        border-radius: 0;
        &:hover {
            transition: 0.2s ease-in;
            background: $warm-grey-25;
        }
    }

    .copy-token-button {
        width: 100%;
        color: $dark-blue-grey;
        transition: 0.2s ease-in;
        box-shadow: none;
        padding: 0;
        :hover {
            transition: 0.2s ease-in;
        }
    }

    .swagger-link {
        color: $uniper-blue;
    }
}

.version {
    font-size: 1.3rem;
    margin: 0.3rem;
}

.copy-text {
    position: fixed;
    top: -99999rem;
}
</style>
